// src/components/OtherPoliciesCard.jsx
// Displays Life Insurance, Ancillary Medical, and Annuity policies for a client

import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Heart, Building2, PiggyBank, Plus, ChevronDown, ChevronUp,
  TrendingDown, Loader2, AlertCircle, RefreshCw
} from 'lucide-react';
import { getOtherPolicies } from '../api';
import LifeInsuranceCard from './policies/LifeInsuranceCard';
import AncillaryPolicyCard from './policies/AncillaryPolicyCard';
import AnnuityCard from './policies/AnnuityCard';
import AddLifeInsuranceModal from './policies/AddLifeInsuranceModal';
import AddAncillaryModal from './policies/AddAncillaryModal';
import AddAnnuityModal from './policies/AddAnnuityModal';

const TABS = [
  { id: 'life', label: 'Life Insurance', icon: Heart, color: 'rose' },
  { id: 'ancillary', label: 'Ancillary Medical', icon: Building2, color: 'blue' },
  { id: 'annuity', label: 'Annuities', icon: PiggyBank, color: 'emerald' }
];

export default function OtherPoliciesCard({ clientId, onPoliciesChange }) {
  const [activeTab, setActiveTab] = useState('life');
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Policy data
  const [lifeInsurance, setLifeInsurance] = useState([]);
  const [ancillary, setAncillary] = useState([]);
  const [annuities, setAnnuities] = useState([]);
  const [summary, setSummary] = useState({ totalActive: 0, riskImpact: 0 });

  // Modals
  const [showAddLife, setShowAddLife] = useState(false);
  const [showAddAncillary, setShowAddAncillary] = useState(false);
  const [showAddAnnuity, setShowAddAnnuity] = useState(false);

  // Fetch policies
  const fetchPolicies = useCallback(async () => {
    if (!clientId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getOtherPolicies(clientId);

      if (data.ok) {
        setLifeInsurance(data.lifeInsurance || []);
        setAncillary(data.ancillary || []);
        setAnnuities(data.annuities || []);
        setSummary(data.summary || { totalActive: 0, riskImpact: 0 });
      }
    } catch (err) {
      console.error('[OtherPoliciesCard] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  // Handle policy changes (refresh data)
  const handlePolicyChange = () => {
    fetchPolicies();
    onPoliciesChange?.();
  };

  // Get policies for current tab
  const getCurrentPolicies = () => {
    switch (activeTab) {
      case 'life': return lifeInsurance;
      case 'ancillary': return ancillary;
      case 'annuity': return annuities;
      default: return [];
    }
  };

  // Get counts per tab
  const getTabCount = (tabId) => {
    switch (tabId) {
      case 'life': return lifeInsurance.filter(p => p.status === 'active').length;
      case 'ancillary': return ancillary.filter(p => p.status === 'active').length;
      case 'annuity': return annuities.filter(p => p.status === 'active').length;
      default: return 0;
    }
  };

  // Open add modal for current tab
  const handleAddClick = () => {
    switch (activeTab) {
      case 'life': setShowAddLife(true); break;
      case 'ancillary': setShowAddAncillary(true); break;
      case 'annuity': setShowAddAnnuity(true); break;
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              Other Policies
              {summary.totalActive > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                  {summary.totalActive} Active
                </span>
              )}
            </h3>
            <p className="text-sm text-slate-500">
              Life, Ancillary Medical & Annuities
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Risk Impact Badge */}
          {summary.riskImpact < 0 && (
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
              <TrendingDown className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">
                {summary.riskImpact} Risk
              </span>
            </div>
          )}

          {/* Expand/Collapse */}
          <button className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-slate-100">
          {/* Tabs */}
          <div className="flex border-b border-slate-100">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const count = getTabCount(tab.id);
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-all ${
                    isActive
                      ? 'text-amber-600 border-b-2 border-amber-500 bg-amber-50/50'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-amber-600' : 'text-slate-400'}`} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  {count > 0 && (
                    <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                      isActive
                        ? 'bg-amber-200 text-amber-700'
                        : 'bg-slate-200 text-slate-600'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Content Area */}
          <div className="p-4">
            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="flex items-center justify-center gap-3 py-8 text-red-500">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm">{error}</span>
                <button
                  onClick={fetchPolicies}
                  className="ml-2 p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Policies List */}
            {!loading && !error && (
              <div className="space-y-3">
                {getCurrentPolicies().length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                      {activeTab === 'life' && <Heart className="w-8 h-8 text-slate-400" />}
                      {activeTab === 'ancillary' && <Building2 className="w-8 h-8 text-slate-400" />}
                      {activeTab === 'annuity' && <PiggyBank className="w-8 h-8 text-slate-400" />}
                    </div>
                    <p className="text-slate-500 mb-4">
                      No {activeTab === 'life' ? 'life insurance' : activeTab === 'ancillary' ? 'ancillary' : 'annuity'} policies yet
                    </p>
                    <button
                      onClick={handleAddClick}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Policy
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Render policies based on active tab */}
                    {activeTab === 'life' && lifeInsurance.map((policy) => (
                      <LifeInsuranceCard
                        key={policy.id}
                        policy={policy}
                        clientId={clientId}
                        onUpdate={handlePolicyChange}
                      />
                    ))}
                    {activeTab === 'ancillary' && ancillary.map((policy) => (
                      <AncillaryPolicyCard
                        key={policy.id}
                        policy={policy}
                        clientId={clientId}
                        onUpdate={handlePolicyChange}
                      />
                    ))}
                    {activeTab === 'annuity' && annuities.map((policy) => (
                      <AnnuityCard
                        key={policy.id}
                        policy={policy}
                        clientId={clientId}
                        onUpdate={handlePolicyChange}
                      />
                    ))}

                    {/* Add Button */}
                    <button
                      onClick={handleAddClick}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-200 hover:border-amber-300 hover:bg-amber-50 text-slate-500 hover:text-amber-600 rounded-xl transition-all"
                    >
                      <Plus className="w-4 h-4" />
                      Add {activeTab === 'life' ? 'Life Insurance' : activeTab === 'ancillary' ? 'Ancillary' : 'Annuity'} Policy
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Risk Impact Footer */}
            {summary.riskImpact < 0 && !loading && (
              <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800">
                      Risk Score Impact: {summary.riskImpact} points
                    </span>
                  </div>
                  <span className="text-xs text-green-600">
                    {summary.totalActive} {summary.totalActive === 1 ? 'policy' : 'policies'} = stronger retention
                  </span>
                </div>
                <div className="mt-2 h-2 bg-green-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all"
                    style={{ width: `${Math.min(Math.abs(summary.riskImpact) * 2.5, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      <AddLifeInsuranceModal
        isOpen={showAddLife}
        onClose={() => setShowAddLife(false)}
        clientId={clientId}
        onSuccess={handlePolicyChange}
      />
      <AddAncillaryModal
        isOpen={showAddAncillary}
        onClose={() => setShowAddAncillary(false)}
        clientId={clientId}
        onSuccess={handlePolicyChange}
      />
      <AddAnnuityModal
        isOpen={showAddAnnuity}
        onClose={() => setShowAddAnnuity(false)}
        clientId={clientId}
        onSuccess={handlePolicyChange}
      />
    </div>
  );
}
